/**
 * WorkoutFinishCard – completed workout summary card
 *
 * Same UI as the Workout page finish screen (checkmark, title, message, stats, optional save status, Done).
 * Used when finishing a workout (Workout page) and when viewing a completed strength workout (Dashboard modal).
 */
import React from 'react';
import '../styles/Workout.css';

const WORKOUT_FINISH_MESSAGES = [
  "You showed up and put in the work. That's what counts.",
  "Another session in the books. Keep building.",
  "Strong work today. Recovery starts now.",
  "You did the hard thing. Be proud of that.",
  "Consistency wins. You're winning.",
  "7 days off makes one weak. Not you.",
  "Begin, begun, begin again.",
  "All you do is win! Well done today.",
  "The iron never lies. Neither do your results.",
  "One rep closer to who you're becoming.",
  "Earned, not given. That's the difference.",
  "Your future self is grateful right now.",
];

function formatTime(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * @param {object} summary – { duration (seconds), completedSets, totalReps, totalWeight }
 * @param {string} [message] – affirming message; if omitted, one is chosen at random
 * @param {'idle'|'saving'|'saved'|'error'} [saveStatus] – if omitted, save-status block is hidden
 * @param {() => void} onDone – called when Done or close is clicked
 * @param {boolean} [showCloseButton=true] – show × overlay close button
 */
function WorkoutFinishCard({ summary, message, saveStatus, onDone, showCloseButton = true }) {
  const msg = message ?? WORKOUT_FINISH_MESSAGES[Math.floor(Math.random() * WORKOUT_FINISH_MESSAGES.length)];
  const showSaveStatus = saveStatus != null && saveStatus !== 'idle';

  return (
    <div className="workout-finish-overlay">
      {showCloseButton && (
        <button
          type="button"
          className="workout-finish-close"
          onClick={onDone}
          aria-label="Close"
        >
          ×
        </button>
      )}
      <div className="workout-finish-screen">
        <div className="workout-finish-content">
          <div className="workout-finish-icon">
            <svg viewBox="0 0 52 52" className="workout-finish-checkmark">
              <circle className="workout-finish-checkmark-circle" cx="26" cy="26" r="25" fill="none" />
              <path className="workout-finish-checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </svg>
          </div>

          <h2 className="workout-finish-title">Workout Complete!</h2>
          <p className="workout-finish-message">{msg}</p>

          <div className="workout-finish-stats">
            <div className="workout-finish-stat">
              <span className="workout-finish-stat-value">{formatTime(summary.duration ?? 0)}</span>
              <span className="workout-finish-stat-label">Duration</span>
            </div>
            <div className="workout-finish-stat">
              <span className="workout-finish-stat-value">{summary.completedSets ?? 0}</span>
              <span className="workout-finish-stat-label">Sets</span>
            </div>
            <div className="workout-finish-stat">
              <span className="workout-finish-stat-value">{summary.totalReps ?? 0}</span>
              <span className="workout-finish-stat-label">Reps</span>
            </div>
            {(summary.totalWeight ?? 0) > 0 && (
              <div className="workout-finish-stat">
                <span className="workout-finish-stat-value">{(summary.totalWeight ?? 0).toLocaleString()}</span>
                <span className="workout-finish-stat-label">Total lbs</span>
              </div>
            )}
          </div>

          {showSaveStatus && (
            <div className="workout-finish-save-status">
              {saveStatus === 'saving' && <span className="workout-finish-saving">Saving workout...</span>}
              {saveStatus === 'saved' && <span className="workout-finish-saved">✓ Workout saved</span>}
              {saveStatus === 'error' && (
                <span className="workout-finish-error">Could not save. Will retry later.</span>
              )}
            </div>
          )}

          <button
            type="button"
            className="c-ripple btn btn--primary workout-finish-done"
            onClick={onDone}
            disabled={saveStatus === 'saving'}
          >
            {saveStatus === 'saving' ? 'Saving...' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WorkoutFinishCard;
